/**
 * FikoCostEngine - Ana Maliyet Hesaplama Motoru
 *
 * Tum uretim surecleri icin gercek fizik-bazli maliyet hesaplama.
 * BREP analiz sonuclari + malzeme veritabani + surec parametreleri
 * kullanarak detayli maliyet raporu uretir.
 */

import { FikoMaterialDB, getMaterialDB, MaterialProperties, ManufacturingProcessType, Currency } from './FikoMaterialDB';
import { FikoCastingCost, CastingCostInput, CastingCostResult } from './FikoCastingCost';
import { FikoCNCCost, CNCCostInput, CNCCostResult } from './FikoCNCCost';
import { FikoSheetMetalCost, SheetMetalCostInput, SheetMetalCostResult } from './FikoSheetMetalCost';
import { FikoInjectionMoldingCost, InjectionMoldingCostInput, InjectionMoldingCostResult } from './FikoInjectionMoldingCost';
import { Fiko3DPrintCost, Print3DCostInput, Print3DCostResult } from './Fiko3DPrintCost';
import { FikoForgingWeldingCost, ForgingCostInput, ForgingCostResult, WeldingCostInput, WeldingCostResult, SurfaceTreatmentCostInput, SurfaceTreatmentCostResult, HeatTreatmentCostInput, HeatTreatmentCostResult } from './FikoForgingWeldingCost';

// ============================================================
// COST ENGINE TYPES
// ============================================================

/** Part geometry from BREP analysis */
export interface PartGeometry {
    volumeCm3: number;           // Hacim (cm3)
    surfaceAreaCm2: number;      // Yuzey alani (cm2)
    boundingBox: {               // Sinir kutusu (mm)
        x: number;
        y: number;
        z: number;
    };
    // Geometri karmasikligi (0-100)
    complexityScore: number;
    // Feature counts
    holeCount: number;
    pocketCount: number;
    filletCount: number;
    chamferCount: number;
    threadCount: number;
    ribCount: number;
    undercutCount: number;
    // Ek geometri bilgileri
    maxWallThickness?: number;    // mm
    minWallThickness?: number;    // mm
    totalEdgeLength?: number;     // mm (kaynak icin)
    perimeterLength?: number;     // mm (sac metal kesim icin)
    bendCount?: number;           // Bukum sayisi (sac metal)
    sheetThickness?: number;      // mm (sac metal kalinligi)
}

/** Complete cost breakdown */
export interface DetailedCostBreakdown {
    // Ana kalemler
    materialCost: number;
    laborCost: number;
    machineCost: number;
    toolingCost: number;        // Kalip/takim maliyeti (amorti)
    setupCost: number;
    postProcessingCost: number;
    qualityControlCost: number;
    overheadCost: number;

    // Alt kalemler
    subItems: CostLineItem[];

    // Toplam
    subtotal: number;
    profitMargin: number;       // % cinsinden
    profitAmount: number;
    totalPerPart: number;
    totalForBatch: number;

    // Meta
    currency: Currency;
    batchSize: number;
    unitWeight: number;         // kg
    materialWastePercent: number;
}

export interface CostLineItem {
    category: string;
    description: string;
    descriptionTR: string;
    quantity: number;
    unitCost: number;
    totalCost: number;
    unit: string;              // 'kg', 'saat', 'adet', 'cm2', etc.
}

/** Cost comparison across processes */
export interface ProcessCostComparison {
    process: ManufacturingProcessType;
    processName: string;
    processNameTR: string;
    isCompatible: boolean;
    costPerPart: number;
    costPerKg: number;
    leadTimeDays: number;
    minBatchSize: number;
    qualityRating: number;     // 0-100
    breakdown: DetailedCostBreakdown;
    notes: string[];
    notesTR: string[];
}

/** Main cost estimation input */
export interface CostEstimationInput {
    // Part data
    partGeometry: PartGeometry;
    materialId: string;

    // Production parameters
    batchSize: number;              // Uretim adedi
    annualVolume?: number;          // Yillik adet (kalip amortisman icin)

    // Target process (optional - all compatible calculated if not set)
    targetProcess?: ManufacturingProcessType;

    // Override parameters (optional)
    profitMarginPercent?: number;   // Varsayilan %15
    overheadPercent?: number;       // Varsayilan %10
    qualityControlPercent?: number; // Varsayilan %3

    // Tolerans & yuzey
    tightestToleranceMM?: number;
    requiredSurfaceRa?: number;     // um

    // Para birimi
    outputCurrency?: Currency;

    // Post-processing (optional)
    requiresHeatTreatment?: boolean;
    surfaceTreatment?: 'anodizing' | 'plating' | 'painting' | 'powder_coating' | 'none';

    // Kaynak (optional)
    weldingLength?: number;         // mm
    weldingType?: 'mig' | 'tig' | 'spot' | 'laser';

    // Sac Metal (optional)
    sheetThickness?: number;        // mm
    bendCount?: number;
    perimeterLength?: number;       // mm
}

/** Full cost estimation result */
export interface CostEstimationResult {
    // Ana sonuc
    recommendedProcess: ManufacturingProcessType;
    recommendedProcessName: string;
    recommendedProcessNameTR: string;
    bestCostPerPart: number;
    bestCostPerKg: number;

    // Detayli analiz
    breakdown: DetailedCostBreakdown;

    // Tum surec karsilastirmasi
    processComparisons: ProcessCostComparison[];

    // Malzeme bilgisi
    material: MaterialProperties;
    partWeightKg: number;
    partVolumeCm3: number;

    // Trafik isigi
    feasibilitySignal: 'green' | 'yellow' | 'red';
    feasibilityNotes: string[];
    feasibilityNotesTR: string[];

    // Meta
    currency: Currency;
    calculatedAt: Date;
}


// ============================================================
// COST ENGINE
// ============================================================

export class FikoCostEngine {
    private materialDB: FikoMaterialDB;
    private castingCost: FikoCastingCost;
    private cncCost: FikoCNCCost;
    private sheetMetalCost: FikoSheetMetalCost;
    private injectionMoldingCost: FikoInjectionMoldingCost;
    private print3dCost: Fiko3DPrintCost;
    private forgingWeldingCost: FikoForgingWeldingCost;

    constructor(materialDB?: FikoMaterialDB) {
        this.materialDB = materialDB || getMaterialDB();
        this.castingCost = new FikoCastingCost(this.materialDB);
        this.cncCost = new FikoCNCCost(this.materialDB);
        this.sheetMetalCost = new FikoSheetMetalCost(this.materialDB);
        this.injectionMoldingCost = new FikoInjectionMoldingCost(this.materialDB);
        this.print3dCost = new Fiko3DPrintCost(this.materialDB);
        this.forgingWeldingCost = new FikoForgingWeldingCost(this.materialDB);
    }

    /**
     * Ana maliyet hesaplama methodu.
     * Parca geometrisi + malzeme + uretim parametreleri ile
     * tum uyumlu surecler icin maliyet hesaplar ve karsilastirir.
     */
    estimate(input: CostEstimationInput): CostEstimationResult {
        const material = this.materialDB.getMaterial(input.materialId);
        if (!material) {
            throw new Error(`Malzeme bulunamadi: ${input.materialId}`);
        }

        const currency = input.outputCurrency || material.priceCurrency;
        const partWeightKg = input.partGeometry.volumeCm3 * material.density;
        const profitMargin = input.profitMarginPercent ?? 15;
        const overheadPercent = input.overheadPercent ?? 10;
        const qcPercent = input.qualityControlPercent ?? 3;

        // Tum uyumlu surecleri hesapla
        const comparisons: ProcessCostComparison[] = [];

        // Casting processes
        for (const castProcess of ['sand_casting', 'die_casting', 'investment_casting', 'shell_mold_casting', 'centrifugal_casting'] as ManufacturingProcessType[]) {
            if (material.compatibleProcesses.includes(castProcess)) {
                const castInput: CastingCostInput = {
                    partGeometry: input.partGeometry,
                    material,
                    batchSize: input.batchSize,
                    annualVolume: input.annualVolume || input.batchSize,
                    castingProcess: castProcess as any,
                    complexityFactor: input.partGeometry.complexityScore / 100,
                    requiresMachining: true,
                    machiningAllowanceMM: 2.0,
                };
                const result = this.castingCost.calculate(castInput);
                comparisons.push(this.buildComparison(castProcess, result.breakdown, material, partWeightKg, currency, profitMargin, overheadPercent, qcPercent, input.batchSize, result.notes, result.notesTR));
            }
        }

        // CNC processes
        for (const cncProcess of ['cnc_milling_3axis', 'cnc_milling_5axis', 'cnc_turning'] as ManufacturingProcessType[]) {
            if (material.compatibleProcesses.includes(cncProcess)) {
                const cncInput: CNCCostInput = {
                    partGeometry: input.partGeometry,
                    material,
                    batchSize: input.batchSize,
                    machiningProcess: cncProcess as any,
                    tightestToleranceMM: input.tightestToleranceMM || 0.1,
                    requiredSurfaceRa: input.requiredSurfaceRa || 3.2,
                };
                const result = this.cncCost.calculate(cncInput);
                comparisons.push(this.buildComparison(cncProcess, result.breakdown, material, partWeightKg, currency, profitMargin, overheadPercent, qcPercent, input.batchSize, result.notes, result.notesTR));
            }
        }

        // Sheet metal
        if (input.sheetThickness && input.perimeterLength) {
            for (const smProcess of ['sheet_metal_laser', 'sheet_metal_plasma', 'sheet_metal_waterjet'] as ManufacturingProcessType[]) {
                if (material.compatibleProcesses.includes(smProcess)) {
                    const smInput: SheetMetalCostInput = {
                        partGeometry: input.partGeometry,
                        material,
                        batchSize: input.batchSize,
                        cuttingProcess: smProcess as any,
                        sheetThickness: input.sheetThickness,
                        perimeterLength: input.perimeterLength,
                        bendCount: input.bendCount || 0,
                        holeCount: input.partGeometry.holeCount,
                    };
                    const result = this.sheetMetalCost.calculate(smInput);
                    comparisons.push(this.buildComparison(smProcess, result.breakdown, material, partWeightKg, currency, profitMargin, overheadPercent, qcPercent, input.batchSize, result.notes, result.notesTR));
                }
            }
        }

        // Injection molding
        if (material.compatibleProcesses.includes('injection_molding')) {
            const imInput: InjectionMoldingCostInput = {
                partGeometry: input.partGeometry,
                material,
                batchSize: input.batchSize,
                annualVolume: input.annualVolume || input.batchSize,
                cavityCount: this.suggestCavityCount(input.batchSize),
            };
            const result = this.injectionMoldingCost.calculate(imInput);
            comparisons.push(this.buildComparison('injection_molding', result.breakdown, material, partWeightKg, currency, profitMargin, overheadPercent, qcPercent, input.batchSize, result.notes, result.notesTR));
        }

        // 3D Printing
        for (const printProcess of ['fdm_3d_print', 'sla_3d_print', 'sls_3d_print', 'dmls_3d_print', 'mjf_3d_print'] as ManufacturingProcessType[]) {
            if (material.compatibleProcesses.includes(printProcess)) {
                const printInput: Print3DCostInput = {
                    partGeometry: input.partGeometry,
                    material,
                    batchSize: input.batchSize,
                    printProcess: printProcess as any,
                };
                const result = this.print3dCost.calculate(printInput);
                comparisons.push(this.buildComparison(printProcess, result.breakdown, material, partWeightKg, currency, profitMargin, overheadPercent, qcPercent, input.batchSize, result.notes, result.notesTR));
            }
        }

        // Forging
        for (const forgeProcess of ['open_die_forging', 'closed_die_forging'] as ManufacturingProcessType[]) {
            if (material.compatibleProcesses.includes(forgeProcess)) {
                const forgeInput: ForgingCostInput = {
                    partGeometry: input.partGeometry,
                    material,
                    batchSize: input.batchSize,
                    annualVolume: input.annualVolume || input.batchSize,
                    forgingProcess: forgeProcess as any,
                };
                const result = this.forgingWeldingCost.calculateForging(forgeInput);
                comparisons.push(this.buildComparison(forgeProcess, result.breakdown, material, partWeightKg, currency, profitMargin, overheadPercent, qcPercent, input.batchSize, result.notes, result.notesTR));
            }
        }

        // Sort by cost
        comparisons.sort((a, b) => a.costPerPart - b.costPerPart);

        // Best result
        const best = comparisons.length > 0 ? comparisons[0] : null;

        // If target process specified, use it
        let recommended = best;
        if (input.targetProcess) {
            const target = comparisons.find(c => c.process === input.targetProcess);
            if (target) recommended = target;
        }

        // Post processing costs (if any)
        if (recommended && (input.requiresHeatTreatment || input.surfaceTreatment !== 'none')) {
            // Add to breakdown
            if (input.requiresHeatTreatment) {
                const htInput: HeatTreatmentCostInput = {
                    partWeightKg,
                    material,
                    treatmentType: 'quench_temper',
                    batchSize: input.batchSize,
                };
                const htResult = this.forgingWeldingCost.calculateHeatTreatment(htInput);
                recommended.breakdown.postProcessingCost += htResult.costPerPart;
                recommended.breakdown.totalPerPart += htResult.costPerPart;
                recommended.breakdown.totalForBatch += htResult.costPerPart * input.batchSize;
            }

            if (input.surfaceTreatment && input.surfaceTreatment !== 'none') {
                const stInput: SurfaceTreatmentCostInput = {
                    surfaceAreaCm2: input.partGeometry.surfaceAreaCm2,
                    material,
                    treatmentType: input.surfaceTreatment,
                    batchSize: input.batchSize,
                };
                const stResult = this.forgingWeldingCost.calculateSurfaceTreatment(stInput);
                recommended.breakdown.postProcessingCost += stResult.costPerPart;
                recommended.breakdown.totalPerPart += stResult.costPerPart;
                recommended.breakdown.totalForBatch += stResult.costPerPart * input.batchSize;
            }
        }

        // Feasibility signal
        const { signal, notes, notesTR } = this.determineFeasibility(input, material, comparisons);

        return {
            recommendedProcess: recommended?.process || 'cnc_milling_3axis',
            recommendedProcessName: recommended?.processName || 'CNC Milling 3-Axis',
            recommendedProcessNameTR: recommended?.processNameTR || 'CNC Frezeleme 3-Eksen',
            bestCostPerPart: recommended?.costPerPart || 0,
            bestCostPerKg: recommended?.costPerKg || 0,
            breakdown: recommended?.breakdown || this.emptyBreakdown(currency, input.batchSize, partWeightKg),
            processComparisons: comparisons,
            material,
            partWeightKg,
            partVolumeCm3: input.partGeometry.volumeCm3,
            feasibilitySignal: signal,
            feasibilityNotes: notes,
            feasibilityNotesTR: notesTR,
            currency,
            calculatedAt: new Date(),
        };
    }

    /** Build a process comparison entry from calculated breakdown */
    private buildComparison(
        process: ManufacturingProcessType,
        lineItems: CostLineItem[],
        material: MaterialProperties,
        partWeightKg: number,
        currency: Currency,
        profitMarginPercent: number,
        overheadPercent: number,
        qcPercent: number,
        batchSize: number,
        notes: string[],
        notesTR: string[],
    ): ProcessCostComparison {
        // Aggregate line items into breakdown categories
        let materialCost = 0, laborCost = 0, machineCost = 0, toolingCost = 0, setupCost = 0, postProcessingCost = 0;

        for (const item of lineItems) {
            switch (item.category) {
                case 'material': materialCost += item.totalCost; break;
                case 'labor': laborCost += item.totalCost; break;
                case 'machine': machineCost += item.totalCost; break;
                case 'tooling': toolingCost += item.totalCost; break;
                case 'setup': setupCost += item.totalCost; break;
                case 'post_processing': postProcessingCost += item.totalCost; break;
                default: machineCost += item.totalCost; break;
            }
        }

        const subtotal = materialCost + laborCost + machineCost + toolingCost + setupCost + postProcessingCost;
        const qcCost = subtotal * (qcPercent / 100);
        const overhead = subtotal * (overheadPercent / 100);
        const withOverhead = subtotal + qcCost + overhead;
        const profit = withOverhead * (profitMarginPercent / 100);
        const totalPerPart = withOverhead + profit;

        const breakdown: DetailedCostBreakdown = {
            materialCost,
            laborCost,
            machineCost,
            toolingCost,
            setupCost,
            postProcessingCost,
            qualityControlCost: qcCost,
            overheadCost: overhead,
            subItems: lineItems,
            subtotal,
            profitMargin: profitMarginPercent,
            profitAmount: profit,
            totalPerPart: Math.round(totalPerPart * 100) / 100,
            totalForBatch: Math.round(totalPerPart * batchSize * 100) / 100,
            currency,
            batchSize,
            unitWeight: partWeightKg,
            materialWastePercent: this.estimateWastePercent(process),
        };

        const names = this.getProcessNames(process);

        return {
            process,
            processName: names.en,
            processNameTR: names.tr,
            isCompatible: true,
            costPerPart: breakdown.totalPerPart,
            costPerKg: partWeightKg > 0 ? breakdown.totalPerPart / partWeightKg : 0,
            leadTimeDays: this.estimateLeadTime(process, batchSize),
            minBatchSize: this.getMinBatchSize(process),
            qualityRating: this.getQualityRating(process),
            breakdown,
            notes,
            notesTR,
        };
    }

    private emptyBreakdown(currency: Currency, batchSize: number, unitWeight: number): DetailedCostBreakdown {
        return {
            materialCost: 0, laborCost: 0, machineCost: 0, toolingCost: 0, setupCost: 0,
            postProcessingCost: 0, qualityControlCost: 0, overheadCost: 0,
            subItems: [], subtotal: 0, profitMargin: 0, profitAmount: 0,
            totalPerPart: 0, totalForBatch: 0, currency, batchSize, unitWeight, materialWastePercent: 0,
        };
    }

    private suggestCavityCount(batchSize: number): number {
        if (batchSize < 1000) return 1;
        if (batchSize < 5000) return 2;
        if (batchSize < 20000) return 4;
        if (batchSize < 100000) return 8;
        return 16;
    }

    private estimateWastePercent(process: ManufacturingProcessType): number {
        const wasteMap: Record<string, number> = {
            sand_casting: 15, die_casting: 8, investment_casting: 5, shell_mold_casting: 12, centrifugal_casting: 10,
            cnc_milling_3axis: 60, cnc_milling_5axis: 55, cnc_turning: 45, cnc_edm: 5, cnc_grinding: 3,
            sheet_metal_laser: 20, sheet_metal_plasma: 22, sheet_metal_waterjet: 18, sheet_metal_punching: 25, sheet_metal_bending: 2,
            injection_molding: 5, blow_molding: 8,
            fdm_3d_print: 10, sla_3d_print: 8, sls_3d_print: 5, dmls_3d_print: 5, mjf_3d_print: 5,
            open_die_forging: 25, closed_die_forging: 15,
            welding_mig: 5, welding_tig: 3, welding_spot: 1, welding_laser: 2,
        };
        return wasteMap[process] || 10;
    }

    private estimateLeadTime(process: ManufacturingProcessType, batchSize: number): number {
        const baseLeadDays: Record<string, number> = {
            sand_casting: 21, die_casting: 42, investment_casting: 28, shell_mold_casting: 21, centrifugal_casting: 14,
            cnc_milling_3axis: 5, cnc_milling_5axis: 7, cnc_turning: 4, cnc_edm: 10, cnc_grinding: 5,
            sheet_metal_laser: 3, sheet_metal_plasma: 3, sheet_metal_waterjet: 4, sheet_metal_punching: 5, sheet_metal_bending: 3,
            injection_molding: 35, blow_molding: 28,
            fdm_3d_print: 2, sla_3d_print: 3, sls_3d_print: 5, dmls_3d_print: 7, mjf_3d_print: 5,
            open_die_forging: 14, closed_die_forging: 28,
        };
        const base = baseLeadDays[process] || 14;
        const volumeFactor = Math.log10(Math.max(1, batchSize / 100)) * 3;
        return Math.ceil(base + volumeFactor);
    }

    private getMinBatchSize(process: ManufacturingProcessType): number {
        const mins: Record<string, number> = {
            sand_casting: 1, die_casting: 500, investment_casting: 10, shell_mold_casting: 50, centrifugal_casting: 5,
            cnc_milling_3axis: 1, cnc_milling_5axis: 1, cnc_turning: 1, cnc_edm: 1, cnc_grinding: 1,
            sheet_metal_laser: 1, sheet_metal_plasma: 1, sheet_metal_waterjet: 1, sheet_metal_punching: 50, sheet_metal_bending: 1,
            injection_molding: 500, blow_molding: 1000,
            fdm_3d_print: 1, sla_3d_print: 1, sls_3d_print: 1, dmls_3d_print: 1, mjf_3d_print: 10,
            open_die_forging: 1, closed_die_forging: 100,
        };
        return mins[process] || 1;
    }

    private getQualityRating(process: ManufacturingProcessType): number {
        const ratings: Record<string, number> = {
            sand_casting: 55, die_casting: 75, investment_casting: 90, shell_mold_casting: 70, centrifugal_casting: 65,
            cnc_milling_3axis: 90, cnc_milling_5axis: 95, cnc_turning: 88, cnc_edm: 92, cnc_grinding: 97,
            sheet_metal_laser: 85, sheet_metal_plasma: 70, sheet_metal_waterjet: 88, sheet_metal_punching: 80, sheet_metal_bending: 75,
            injection_molding: 85, blow_molding: 70,
            fdm_3d_print: 60, sla_3d_print: 80, sls_3d_print: 78, dmls_3d_print: 85, mjf_3d_print: 82,
            open_die_forging: 70, closed_die_forging: 80,
        };
        return ratings[process] || 70;
    }

    private getProcessNames(process: ManufacturingProcessType): { en: string; tr: string } {
        const names: Record<string, { en: string; tr: string }> = {
            sand_casting: { en: 'Sand Casting', tr: 'Kum Dokum' },
            die_casting: { en: 'Die Casting', tr: 'Basincli Dokum' },
            investment_casting: { en: 'Investment Casting', tr: 'Hassas Dokum (Microfusion)' },
            shell_mold_casting: { en: 'Shell Mold Casting', tr: 'Kabuk Kalip Dokum' },
            centrifugal_casting: { en: 'Centrifugal Casting', tr: 'Savurma Dokum' },
            cnc_milling_3axis: { en: 'CNC Milling 3-Axis', tr: 'CNC Frezeleme 3-Eksen' },
            cnc_milling_5axis: { en: 'CNC Milling 5-Axis', tr: 'CNC Frezeleme 5-Eksen' },
            cnc_turning: { en: 'CNC Turning', tr: 'CNC Tornalama' },
            cnc_edm: { en: 'EDM (Wire/Sinker)', tr: 'Elektro-Erozyon (Tel/Dalma)' },
            cnc_grinding: { en: 'Grinding', tr: 'Taslama' },
            sheet_metal_laser: { en: 'Laser Cutting', tr: 'Lazer Kesim' },
            sheet_metal_plasma: { en: 'Plasma Cutting', tr: 'Plazma Kesim' },
            sheet_metal_waterjet: { en: 'Waterjet Cutting', tr: 'Su Jeti Kesim' },
            sheet_metal_punching: { en: 'CNC Punching', tr: 'CNC Zimbalama' },
            sheet_metal_bending: { en: 'Press Brake Bending', tr: 'Abkant Bukme' },
            injection_molding: { en: 'Injection Molding', tr: 'Enjeksiyon Kaliplama' },
            blow_molding: { en: 'Blow Molding', tr: 'Uflemeli Kaliplama' },
            fdm_3d_print: { en: 'FDM 3D Printing', tr: 'FDM 3D Baski' },
            sla_3d_print: { en: 'SLA 3D Printing', tr: 'SLA 3D Baski' },
            sls_3d_print: { en: 'SLS 3D Printing', tr: 'SLS 3D Baski' },
            dmls_3d_print: { en: 'DMLS Metal 3D Printing', tr: 'DMLS Metal 3D Baski' },
            mjf_3d_print: { en: 'MJF 3D Printing', tr: 'MJF 3D Baski' },
            open_die_forging: { en: 'Open Die Forging', tr: 'Acik Kalip Dovme' },
            closed_die_forging: { en: 'Closed Die Forging', tr: 'Kapali Kalip Dovme' },
            welding_mig: { en: 'MIG Welding', tr: 'MIG Kaynak' },
            welding_tig: { en: 'TIG Welding', tr: 'TIG Kaynak' },
            welding_spot: { en: 'Spot Welding', tr: 'Nokta Kaynak' },
            welding_laser: { en: 'Laser Welding', tr: 'Lazer Kaynak' },
            heat_treatment: { en: 'Heat Treatment', tr: 'Isil Islem' },
            surface_anodizing: { en: 'Anodizing', tr: 'Anodizasyon' },
            surface_plating: { en: 'Plating', tr: 'Kaplama' },
            surface_painting: { en: 'Painting', tr: 'Boyama' },
            surface_powder_coating: { en: 'Powder Coating', tr: 'Toz Boya' },
        };
        return names[process] || { en: process, tr: process };
    }

    private determineFeasibility(
        input: CostEstimationInput,
        material: MaterialProperties,
        comparisons: ProcessCostComparison[]
    ): { signal: 'green' | 'yellow' | 'red'; notes: string[]; notesTR: string[] } {
        const notes: string[] = [];
        const notesTR: string[] = [];

        if (comparisons.length === 0) {
            return { signal: 'red', notes: ['No compatible process found'], notesTR: ['Uyumlu uretim sureci bulunamadi'] };
        }

        let signal: 'green' | 'yellow' | 'red' = 'green';

        // Check batch vs min batch
        for (const c of comparisons) {
            if (input.batchSize < c.minBatchSize) {
                signal = 'yellow';
                notes.push(`${c.processName}: batch size ${input.batchSize} is below minimum ${c.minBatchSize}`);
                notesTR.push(`${c.processNameTR}: uretim adedi ${input.batchSize}, minimum ${c.minBatchSize}'in altinda`);
            }
        }

        // Check wall thickness
        if (input.partGeometry.minWallThickness !== undefined) {
            const minWT = input.partGeometry.minWallThickness;
            if (input.targetProcess) {
                const processMinWT = material.minWallThickness[input.targetProcess];
                if (processMinWT && minWT < processMinWT) {
                    signal = 'red';
                    notes.push(`Min wall thickness ${minWT}mm is below process minimum ${processMinWT}mm`);
                    notesTR.push(`Min duvar kalinligi ${minWT}mm, surec minimumu ${processMinWT}mm'nin altinda`);
                }
            }
        }

        // Check undercuts for casting/molding
        if (input.partGeometry.undercutCount > 0 && input.targetProcess) {
            if (['sand_casting', 'die_casting', 'injection_molding'].includes(input.targetProcess)) {
                signal = signal === 'red' ? 'red' : 'yellow';
                notes.push(`${input.partGeometry.undercutCount} undercuts detected - may require side actions`);
                notesTR.push(`${input.partGeometry.undercutCount} altkes tespit edildi - yan cekici gerekebilir`);
            }
        }

        // Complexity warning
        if (input.partGeometry.complexityScore > 80) {
            signal = signal === 'red' ? 'red' : 'yellow';
            notes.push('High geometric complexity - review manufacturing feasibility');
            notesTR.push('Yuksek geometrik karmasiklik - uretilebilirlik gozden gecirilmeli');
        }

        return { signal, notes, notesTR };
    }
}

// ============================================================
// SINGLETON
// ============================================================

let _costEngine: FikoCostEngine | null = null;

export function getCostEngine(): FikoCostEngine {
    if (!_costEngine) {
        _costEngine = new FikoCostEngine();
    }
    return _costEngine;
}

export function disposeCostEngine(): void {
    _costEngine = null;
}
